import { useState } from 'react';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { WorkerComment, WorkersV2API } from '@/services/workersV2API';
import AddCommentModal from './AddCommentModal';

type Props = {
  workerId: number;
  comments: WorkerComment[];
  onCommentAdded: () => void;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const CommentsCard = ({ workerId, comments, onCommentAdded }: Props) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const handleAddComment = async (body: string) => {
    const response = await WorkersV2API.addComment(workerId, body);
    if (!response.success) {
      throw new Error('Failed to add comment');
    }
    onCommentAdded();
  };

  return (
    <>
      {showAddModal && (
        <AddCommentModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddComment}
        />
      )}

      <Card
        actions={[
          {
            label: 'Add',
            onAction: () => setShowAddModal(true),
          },
        ]}
        title="Comments"
      >
        <Card.Section>
          {!comments || comments.length === 0 ? (
            <Stack style={{ justifyContent: 'center', padding: '20px' }}>
              <Small color="lighter">No comments yet</Small>
            </Stack>
          ) : (
            <Stack vertical gap={16}>
              {comments.map((comment) => (
                <Stack key={comment.id} vertical gap={4}>
                  <Body>{comment.body}</Body>
                  <Small color="theme">
                    {formatDate(comment.createdAt)}
                    {comment.actor && (
                      <>
                        {' '}
                        &mdash; {comment.actor.firstName}{' '}
                        {comment.actor.lastName}
                      </>
                    )}
                  </Small>
                </Stack>
              ))}
            </Stack>
          )}
        </Card.Section>
      </Card>
    </>
  );
};

export default CommentsCard;
